// ============================================================================
// patientCabinetDashboard.ts — ДАННЫЕ кабинета пациента «ГидраМедик»
// (ядро medic, психология psy, ЭКГ). SSoT — СЕРВЕР: здесь только чтение
// существующими RPC (новых контрактов/миграций нет) и явные состояния.
//
// Разделы кабинета:
//   • тесты/результаты — назначения и протоколы (psy_assignments + типы
//     опросов; без модуля psy — общий кабинет опросов, как раньше);
//   • записи/приёмы — medic_encounters (карточка пациента ядра) и psy_bookings
//     (запись к специалисту, если модуль установлен);
//   • ЭКГ — cardio_studies: по реквизиту пациента, иначе — СВОИ исследования
//     (владелец записи, OwnScope);
//   • доступ/приглашения — состояние связи учётной записи с карточкой,
//     доступность разделов (SSoT прав — сервер) и выданные ссылки/коды на тесты.
//
// ВАЖНО: права роли «Пациент» выдаёт администратор базы. Нет права — раздел
// честно помечается `denied` (не пустой список и не «вечная загрузка»).
// Кабинет НИЧЕГО не пишет в медицинские данные: только чтение (прохождение
// теста — отдельный существующий механизм PollService).
// ============================================================================

import type { TableDataRow } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { friendlyError, isNotFoundError, isPermissionDeniedError, logSwallowed } from '@/lib/errors';
import {
    activeLink,
    assignmentStatusLabel,
    hasPsyAssignmentsModule,
    loadAssignments,
    loadMethods,
    loadPatientResultsForMethods,
    shortCode,
    type PatientResult,
    type PsyAssignment,
    type PsyMethod,
} from '@/lib/psyWorkspace';
import { loadPatientCabinet, type PatientCabinetData } from '@/lib/patientCabinet';
import {
    loadAppointmentEnumLabels,
    loadAppointments,
    type CabinetAppointment,
} from '@/lib/cabinet/appointments';
import {
    listEcgStudies,
    resolveEcgPatientColumn,
    type EcgStudyListItem,
} from '@/lib/ecgStudies';

export const PSY_CLIENTS_TABLE = 'psy_clients';
export const MEDIC_PATIENTS_TABLE = 'medic_patients';
export const PSY_BOOKINGS_TABLE = 'psy_bookings';
export const PSY_BOOKING_STATUS_ENUM = 'PsyStatusZapisi';

/** Состояние источника данных: права — SSoT сервера. */
export type CabinetSourceState = 'ready' | 'denied' | 'unavailable' | 'error';

/** Состояние связи учётной записи с карточкой (у пациента карточки может не быть). */
export type CabinetIdentityState = CabinetSourceState | 'unlinked' | 'ambiguous';

/** Как карточка сопоставлена с учётной записью. */
export type CabinetMatch = 'account' | 'phone' | 'email' | '';

/** Человек (карточка клиента psy или пациента ядра), найденный для учётной записи. */
export interface CabinetPerson {
    id: string;
    name: string;
    phone: string;
    email: string;
    /** Привязка к учётной записи, если реквизит есть в базе. */
    linkedUserId: string;
    matchedBy: CabinetMatch;
}

export interface CabinetIdentity {
    userId: string;
    name: string;
    login: string;
    phone: string;
    email: string;
    roles: string[];
    isAdmin: boolean;
    patientRole: boolean;
    psy: CabinetPerson | null;
    psyState: CabinetIdentityState;
    psyError: string;
    psyNote: string;
    medic: CabinetPerson | null;
    medicState: CabinetIdentityState;
    medicError: string;
    medicNote: string;
}

/** Тесты/результаты: psy-назначения либо общий кабинет опросов. */
export interface CabinetTests {
    kind: 'psy' | 'poll' | 'none';
    state: CabinetIdentityState;
    error: string;
    note: string;
    methods: PsyMethod[];
    assignments: PsyAssignment[];
    results: PatientResult[];
    assignmentsState: CabinetSourceState;
    assignmentsError: string;
    resultsState: CabinetSourceState;
    resultsError: string;
    /** Общий кабинет опросов (модуль psy не установлен / нет карточки клиента). */
    poll: PatientCabinetData | null;
}

/** Приёмы ядра «ГидраМедик» (medic_encounters). */
export interface CabinetEncounters {
    state: CabinetSourceState;
    items: CabinetAppointment[];
    statusLabels: Record<string, string>;
    typeLabels: Record<string, string>;
    error: string;
    note: string;
}

/** Запись на приём к специалисту (psy_bookings). */
export interface CabinetBooking {
    id: string;
    startAt: string;
    durationMin: number | null;
    serviceName: string;
    specialistName: string;
    statusCode: string;
    statusLabel: string;
    comment: string;
}

export interface CabinetBookings {
    state: CabinetSourceState;
    items: CabinetBooking[];
    error: string;
    note: string;
}

/** История ЭКГ (структурно совместима с PatientCardEcgSection). */
export interface CabinetEcg {
    state: CabinetSourceState;
    studies: EcgStudyListItem[];
    error: string;
    note: string;
}

/** Приглашение на тестирование (выданная специалистом ссылка/код). */
export interface CabinetInvitation {
    id: string;
    methodName: string;
    methodCode: string;
    status: string;
    statusLabel: string;
    code: string;
    token: string;
    expiresAt: string;
    active: boolean;
    /** Ссылка/код выдавались, но срок действия истёк. */
    expired: boolean;
}

export interface CabinetAccessRow {
    key: string;
    title: string;
    state: CabinetIdentityState;
    detail: string;
}

export interface PatientCabinetDashboard {
    identity: CabinetIdentity;
    tests: CabinetTests;
    encounters: CabinetEncounters;
    bookings: CabinetBookings;
    ecg: CabinetEcg;
    invitations: CabinetInvitation[];
    access: CabinetAccessRow[];
}

type Row = Record<string, string> & { id: string };

function cellsOf(row: TableDataRow): Record<string, string> {
    const out: Record<string, string> = {};
    for (const value of row.values ?? []) {
        out[String(value.column)] = value.value === null || value.value === undefined
            ? ''
            : String(value.value);
    }
    return out;
}

function rowsOf(rows: TableDataRow[]): Row[] {
    return rows.map((row) => ({ id: String(row.id || ''), ...cellsOf(row) }));
}

function digitsOf(value: string): string {
    return String(value || '').replace(/\D+/g, '');
}

/** Сравнение телефонов по последним 10 цифрам (форматы +7/8/скобки/дефисы). */
function phoneKeyOf(value: string): string {
    const digits = digitsOf(value);
    return digits.length > 10 ? digits.slice(-10) : digits;
}

function normalizeEmail(value: string): string {
    return String(value || '').trim().toLowerCase();
}

function personName(row: Row): string {
    const full = [row.last_name, row.first_name, row.middle_name]
        .map((part) => String(part || '').trim())
        .filter(Boolean)
        .join(' ');
    return String(row.name || row.fio || full || '');
}

function personFromRow(row: Row, matchedBy: CabinetMatch): CabinetPerson {
    return {
        id: row.id,
        name: personName(row),
        phone: String(row.phone || ''),
        email: String(row.email || ''),
        linkedUserId: String(row.user_id || ''),
        matchedBy,
    };
}

/** Ошибка источника → явное состояние (права и отсутствие модуля различаются). */
function sourceState(e: unknown): { state: CabinetSourceState; error: string } {
    if (isPermissionDeniedError(e)) return { state: 'denied', error: 'Нет прав на чтение данных' };
    if (isNotFoundError(e)) return { state: 'unavailable', error: '' };
    return { state: 'error', error: friendlyError(e) };
}

function identityState(e: unknown): { state: CabinetIdentityState; error: string } {
    const mapped = sourceState(e);
    return { state: mapped.state, error: mapped.error };
}

/** Состояние источника для разделов, где «нет карточки» = данные недоступны. */
function asSourceState(state: CabinetIdentityState): CabinetSourceState {
    if (state === 'ready' || state === 'denied' || state === 'unavailable' || state === 'error') {
        return state;
    }
    return 'unavailable';
}

function timestampKey(value: string): string {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return new Date(parsed).toISOString();
    return String(value || '');
}

/**
 * Предел выборки точечного запроса по контакту. Совпадений по одному телефону
 * или почте не может быть много; больше одной карточки — уже неоднозначность.
 */
const CONTACT_LOOKUP_LIMIT = 100;

interface ContactLookup {
    column: string;
    value: string;
}

/**
 * Варианты записи телефона для ТОЧЕЧНОГО серверного отбора (сервер фильтрует
 * только равенством). Сравнение всё равно идёт по последним 10 цифрам —
 * варианты лишь расширяют попадание в разные форматы карточки.
 */
function phoneQueryVariants(value: string): string[] {
    const raw = String(value || '').trim();
    const digits = digitsOf(raw);
    const last10 = digits.length > 10 ? digits.slice(-10) : digits;
    const variants = new Set<string>();
    if (raw) variants.add(raw);
    if (digits) variants.add(digits);
    if (last10.length === 10) {
        variants.add(last10);
        variants.add(`7${last10}`);
        variants.add(`8${last10}`);
        variants.add(`+7${last10}`);
        const a = last10.slice(0, 3);
        const b = last10.slice(3, 6);
        const c = last10.slice(6, 8);
        const d = last10.slice(8, 10);
        variants.add(`+7 (${a}) ${b}-${c}-${d}`);
        variants.add(`8 (${a}) ${b}-${c}-${d}`);
    }
    return [...variants];
}

function emailQueryVariants(value: string): string[] {
    const raw = String(value || '').trim();
    const lower = normalizeEmail(raw);
    const variants = new Set<string>();
    if (raw) variants.add(raw);
    if (lower) variants.add(lower);
    return [...variants];
}

async function queryContactRows(table: string, lookup: ContactLookup): Promise<Row[]> {
    const response = await getSDK().metadata.listTableData({
        schema: 'public',
        table,
        pageSize: CONTACT_LOOKUP_LIMIT,
        page: 0,
        filters: [{ column: lookup.column, value: lookup.value }],
    });
    return rowsOf(response.rows);
}

/**
 * resolveByContacts — найти карточку по привязке user_id (если реквизит есть)
 * либо по нормализованному телефону/почте.
 *
 * ВАЖНО (медицинские данные): список карточек НЕ выгружается целиком. Запросы
 * ТОЧЕЧНЫЕ по user_id/телефону/почте — сервер возвращает только кандидатов.
 * Так роль с правом чтения справочника не получает в браузер чужие карточки
 * (даже если таблица без owner-изоляции). Несколько совпадений НЕ сливаются:
 * кабинет просит уточнить у специалиста.
 */
async function resolveByContacts(
    table: string,
    userId: string,
    phone: string,
    email: string,
): Promise<{ person: CabinetPerson | null; state: CabinetIdentityState; error: string; count: number }> {
    const phoneKey = phoneKeyOf(phone);
    const emailKey = normalizeEmail(email);
    if (!userId && !phoneKey && !emailKey) {
        return { person: null, state: 'unlinked', error: '', count: 0 };
    }

    const candidates = new Map<string, CabinetPerson>();
    const addCandidates = (rows: Row[]): void => {
        for (const row of rows) {
            if (!row.id) continue;
            // Карточка, привязанная к ДРУГОЙ учётной записи, не подменяется.
            if (row.user_id && row.user_id !== userId) continue;
            const accountMatch = !!userId && row.user_id === userId;
            const phoneMatch = !!phoneKey && phoneKeyOf(row.phone) === phoneKey;
            const emailMatch = !!emailKey && normalizeEmail(row.email) === emailKey;
            if (!accountMatch && !phoneMatch && !emailMatch) continue;
            const matchedBy: CabinetMatch = accountMatch ? 'account' : phoneMatch ? 'phone' : 'email';
            const existing = candidates.get(row.id);
            if (!existing || (existing.matchedBy !== 'account' && matchedBy === 'account')) {
                candidates.set(row.id, personFromRow(row, matchedBy));
            }
        }
    };

    // Пробный запрос: отличает «нет прав/нет таблицы» от «карточки не связаны»
    // ДО серии запросов по форматам контактов.
    const rawPhone = String(phone || '').trim();
    const rawEmail = String(email || '').trim();
    const hasContacts = !!rawPhone || !!rawEmail;
    // Реквизит user_id есть только у psy_clients; у medic_patients связь с
    // учётной записью определяется контактами (без контактов — не связываем).
    const hasAccountColumn = table === PSY_CLIENTS_TABLE;
    if (!hasContacts && !hasAccountColumn) {
        return { person: null, state: 'unlinked', error: '', count: 0 };
    }
    const probe: ContactLookup = rawPhone
        ? { column: 'phone', value: rawPhone }
        : rawEmail
            ? { column: 'email', value: rawEmail }
            : { column: 'user_id', value: userId };
    try {
        addCandidates(await queryContactRows(table, probe));
    } catch (e) {
        if (isPermissionDeniedError(e) || isNotFoundError(e)) {
            const mapped = identityState(e);
            return { person: null, state: mapped.state, error: mapped.error, count: 0 };
        }
        // Колонки user_id/формата контакта может не быть в старой базе —
        // пробуем остальные точечные запросы (не глушим ошибку молча).
        logSwallowed(e, `patientCabinet: поиск карточки ${table} по ${probe.column}`);
    }

    const lookups: ContactLookup[] = [];
    if (userId && hasAccountColumn) lookups.push({ column: 'user_id', value: userId });
    for (const value of phoneQueryVariants(phone)) lookups.push({ column: 'phone', value });
    for (const value of emailQueryVariants(email)) lookups.push({ column: 'email', value });

    const seen = new Set<string>();
    const unique = lookups.filter((lookup) => {
        const key = `${lookup.column}\u0000${lookup.value}`;
        if (lookup.value === probe.value && lookup.column === probe.column) return false;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    let denied = false;
    let unavailable = false;
    let lookupError: unknown = null;
    const settled = await Promise.allSettled(unique.map((lookup) => queryContactRows(table, lookup)));
    for (const result of settled) {
        if (result.status === 'fulfilled') {
            addCandidates(result.value);
        } else if (isPermissionDeniedError(result.reason)) {
            denied = true;
        } else if (isNotFoundError(result.reason)) {
            unavailable = true;
        } else if (!lookupError) {
            lookupError = result.reason;
        }
    }

    if (candidates.size > 1) return { person: null, state: 'ambiguous', error: '', count: candidates.size };
    if (candidates.size === 1) {
        const [person] = [...candidates.values()];
        return { person, state: 'ready', error: '', count: 1 };
    }
    if (denied) return { person: null, state: 'denied', error: 'Нет прав на чтение картотеки', count: 0 };
    if (lookupError) {
        return { person: null, state: 'error', error: friendlyError(lookupError), count: 0 };
    }
    if (unavailable) return { person: null, state: 'unavailable', error: '', count: 0 };
    return { person: null, state: 'unlinked', error: '', count: 0 };
}

/** Карточка клиента психолога: сначала ЯВНАЯ привязка user_id, затем контакты. */
async function resolvePsyIdentity(
    userId: string,
    phone: string,
    email: string,
): Promise<Pick<CabinetIdentity, 'psy' | 'psyState' | 'psyError' | 'psyNote'>> {
    if (!userId) {
        return { psy: null, psyState: 'error', psyError: 'Не определён текущий пользователь', psyNote: '' };
    }
    try {
        const response = await getSDK().metadata.listTableData({
            schema: 'public',
            table: PSY_CLIENTS_TABLE,
            pageSize: 50,
            page: 0,
            filters: [{ column: 'user_id', value: userId }],
        });
        const linked = rowsOf(response.rows).filter((row) => row.id && row.user_id === userId);
        if (linked.length > 1) {
            return {
                psy: null,
                psyState: 'ambiguous',
                psyError: '',
                psyNote: 'К учётной записи привязано несколько карточек клиента — обратитесь к специалисту.',
            };
        }
        if (linked.length === 1) {
            return {
                psy: personFromRow(linked[0], 'account'),
                psyState: 'ready',
                psyError: '',
                psyNote: 'Карточка клиента связана с учётной записью.',
            };
        }
    } catch (e) {
        if (isPermissionDeniedError(e)) {
            return { psy: null, psyState: 'denied', psyError: 'Нет прав на чтение карточки клиента', psyNote: '' };
        }
        if (isNotFoundError(e)) {
            return {
                psy: null,
                psyState: 'unavailable',
                psyError: '',
                psyNote: 'Модуль психологии не установлен (справочник psy_clients отсутствует).',
            };
        }
        // Колонка user_id в старой базе могла отсутствовать — пробуем контакты,
        // но транспортные/серверные ошибки не маскируем.
        logSwallowed(e, 'patientCabinet: явная привязка psy_clients.user_id');
    }

    const byContacts = await resolveByContacts(PSY_CLIENTS_TABLE, userId, phone, email);
    if (byContacts.state === 'ready' && byContacts.person) {
        return {
            psy: byContacts.person,
            psyState: 'ready',
            psyError: '',
            psyNote: byContacts.person.matchedBy === 'account'
                ? 'Карточка клиента связана с учётной записью.'
                : byContacts.person.matchedBy === 'phone'
                    ? 'Карточка клиента найдена по телефону учётной записи.'
                    : 'Карточка клиента найдена по почте учётной записи.',
        };
    }
    if (byContacts.state === 'ambiguous') {
        return {
            psy: null,
            psyState: 'ambiguous',
            psyError: '',
            psyNote: `Найдено несколько карточек клиента (${byContacts.count}) с вашими контактами — обратитесь к специалисту.`,
        };
    }
    if (byContacts.state === 'unlinked') {
        return {
            psy: null,
            psyState: 'unlinked',
            psyError: '',
            psyNote: 'Карточка клиента не связана с учётной записью — её связывает специалист.',
        };
    }
    return {
        psy: null,
        psyState: byContacts.state,
        psyError: byContacts.error,
        psyNote: byContacts.state === 'denied'
            ? 'Нет прав на чтение карточки клиента.'
            : '',
    };
}

/** Карточка пациента ядра «ГидраМедик» (medic_patients) — по контактам учётной записи. */
async function resolveMedicIdentity(
    userId: string,
    phone: string,
    email: string,
): Promise<Pick<CabinetIdentity, 'medic' | 'medicState' | 'medicError' | 'medicNote'>> {
    const byContacts = await resolveByContacts(MEDIC_PATIENTS_TABLE, userId, phone, email);
    if (byContacts.state === 'ready' && byContacts.person) {
        return {
            medic: byContacts.person,
            medicState: 'ready',
            medicError: '',
            medicNote: byContacts.person.matchedBy === 'account'
                ? 'Карточка пациента связана с учётной записью.'
                : byContacts.person.matchedBy === 'phone'
                    ? 'Карточка пациента найдена по телефону учётной записи.'
                    : 'Карточка пациента найдена по почте учётной записи.',
        };
    }
    if (byContacts.state === 'ambiguous') {
        return {
            medic: null,
            medicState: 'ambiguous',
            medicError: '',
            medicNote: `Найдено несколько карточек пациента (${byContacts.count}) с вашими контактами — обратитесь к администратору.`,
        };
    }
    if (byContacts.state === 'unlinked') {
        return {
            medic: null,
            medicState: 'unlinked',
            medicError: '',
            medicNote: 'Карточка пациента не найдена по контактам учётной записи — её связывает специалист.',
        };
    }
    return {
        medic: null,
        medicState: byContacts.state,
        medicError: byContacts.error,
        medicNote: byContacts.state === 'denied'
            ? 'Нет прав на чтение картотеки пациентов — записи и ЭКГ роли недоступны.'
            : byContacts.state === 'unavailable'
                ? 'Ядро «ГидраМедик» не установлено (справочник medic_patients отсутствует).'
                : '',
    };
}

/** Единая загрузка тестов/результатов (psy при наличии карточки, иначе опросы). */
async function loadTests(
    userId: string,
    identity: CabinetIdentity,
    psyAvailable: boolean,
): Promise<CabinetTests> {
    const empty: CabinetTests = {
        kind: 'none',
        state: 'unavailable',
        error: '',
        note: '',
        methods: [],
        assignments: [],
        results: [],
        assignmentsState: 'unavailable',
        assignmentsError: '',
        resultsState: 'unavailable',
        resultsError: '',
        poll: null,
    };

    if (psyAvailable && identity.psy) {
        let methods: PsyMethod[] = [];
        let methodsState: CabinetSourceState = 'ready';
        let stateError = '';
        try {
            methods = await loadMethods();
        } catch (e) {
            const mapped = sourceState(e);
            methodsState = mapped.state;
            stateError = mapped.error;
            if (mapped.state === 'denied') {
                return {
                    ...empty,
                    kind: 'psy',
                    state: 'denied',
                    error: 'Нет прав на чтение банка методик',
                    note: 'Права роли «Пациент» на методики/протоколы выдаёт администратор базы.',
                };
            }
        }

        if (methodsState !== 'ready') {
            return {
                ...empty,
                kind: 'psy',
                state: methodsState,
                error: stateError,
                note: methodsState === 'unavailable'
                    ? 'Банк методик недоступен (типы опросов не найдены).'
                    : 'Не удалось загрузить банк методик.',
            };
        }

        let assignments: PsyAssignment[] = [];
        let assignmentsState: CabinetSourceState = 'ready';
        let assignmentsError = '';
        try {
            assignments = await loadAssignments(identity.psy.id);
        } catch (e) {
            const mapped = sourceState(e);
            assignmentsState = mapped.state;
            assignmentsError = mapped.error || (mapped.state === 'denied'
                ? 'Нет прав на чтение назначений'
                : mapped.state === 'unavailable'
                    ? 'Справочник назначений недоступен'
                    : 'Не удалось загрузить назначения');
        }

        let results: PatientResult[] = [];
        let resultsState: CabinetSourceState;
        let resultsError = '';
        try {
            const loaded = await loadPatientResultsForMethods(methods, identity.psy.id);
            results = loaded.items;
            resultsState = loaded.denied ? 'denied' : 'ready';
            if (loaded.denied) {
                resultsError = 'Просмотр части результатов ограничен правами роли.';
            }
        } catch (e) {
            const mapped = sourceState(e);
            resultsState = mapped.state;
            resultsError = mapped.error;
        }

        const notes: string[] = [];
        if (assignmentsState !== 'ready') notes.push(assignmentsError);
        if (resultsState === 'denied') notes.push(resultsError);
        return {
            kind: 'psy',
            state: 'ready',
            error: '',
            note: notes.filter(Boolean).join(' '),
            methods,
            assignments,
            results,
            assignmentsState,
            assignmentsError,
            resultsState,
            resultsError,
            poll: null,
        };
    }

    if (psyAvailable) {
        // Карточка клиента не связана/недоступна, но общий механизм опросов
        // может быть назначен пользователю по ролям. Это ДРУГОЙ источник
        // (PollService, права протоколов — OwnScope), поэтому показываем его,
        // если есть назначения или результаты.
        try {
            const poll = await loadPatientCabinet(userId);
            if (poll.tests.length > 0 || poll.protocols.length > 0) {
                return {
                    ...empty,
                    kind: 'poll',
                    state: 'ready',
                    note: identity.psyNote
                        ? `${identity.psyNote} Показаны тесты, назначенные учётной записи.`
                        : '',
                    assignmentsState: 'unavailable',
                    resultsState: poll.protocolsDenied ? 'denied' : 'ready',
                    resultsError: poll.protocolsDenied
                        ? 'Просмотр протоколов роли не разрешён — результаты ведёт специалист.'
                        : '',
                    poll,
                };
            }
        } catch (e) {
            logSwallowed(e, 'patientCabinet: общий кабинет опросов (карточка клиента недоступна)');
        }
        return {
            ...empty,
            kind: 'none',
            state: identity.psyState,
            error: identity.psyError,
            note: identity.psyNote,
        };
    }

    try {
        const poll = await loadPatientCabinet(userId);
        return {
            ...empty,
            kind: 'poll',
            state: 'ready',
            note: '',
            assignmentsState: 'unavailable',
            resultsState: poll.protocolsDenied ? 'denied' : 'ready',
            resultsError: poll.protocolsDenied
                ? 'Просмотр протоколов роли не разрешён — результаты ведёт специалист.'
                : '',
            poll,
        };
    } catch (e) {
        const mapped = sourceState(e);
        return {
            ...empty,
            state: mapped.state,
            error: mapped.error,
            note: mapped.state === 'unavailable'
                ? 'Тесты и опросы в базе не найдены.'
                : mapped.state === 'denied'
                    ? 'Нет прав на тесты и результаты.'
                    : '',
        };
    }
}

async function loadEncounters(identity: CabinetIdentity): Promise<CabinetEncounters> {
    const empty: CabinetEncounters = {
        state: 'unavailable',
        items: [],
        statusLabels: {},
        typeLabels: {},
        error: '',
        note: '',
    };
    if (!identity.medic) {
        return {
            ...empty,
            state: asSourceState(identity.medicState),
            error: identity.medicError,
            note: identity.medicNote || 'Карточка пациента не связана — приёмы недоступны.',
        };
    }
    try {
        const items = await loadAppointments(identity.medic.id);
        let statusLabels: Record<string, string> = {};
        let typeLabels: Record<string, string> = {};
        try {
            const labels = await loadAppointmentEnumLabels();
            statusLabels = labels.status;
            typeLabels = labels.type;
        } catch (e) {
            logSwallowed(e, 'Кабинет пациента: подписи приёмов');
        }
        items.sort((a, b) => {
            const aKey = timestampKey(a.appointmentAt);
            const bKey = timestampKey(b.appointmentAt);
            const now = timestampKey(new Date().toISOString());
            const aUpcoming = aKey >= now;
            const bUpcoming = bKey >= now;
            if (aUpcoming && bUpcoming) return aKey.localeCompare(bKey);
            if (aUpcoming) return -1;
            if (bUpcoming) return 1;
            return bKey.localeCompare(aKey);
        });
        return { state: 'ready', items, statusLabels, typeLabels, error: '', note: '' };
    } catch (e) {
        const mapped = sourceState(e);
        return {
            ...empty,
            state: mapped.state,
            error: mapped.error || (mapped.state === 'denied' ? 'Нет прав на чтение приёмов' : ''),
            note: mapped.state === 'unavailable'
                ? 'Документ приёмов не найден (medic_encounters).'
                : mapped.state === 'denied'
                    ? 'Приёмы недоступны роли «Пациент» — право «Чтение» выдаёт администратор базы.'
                    : '',
        };
    }
}

async function loadBookings(psyAvailable: boolean, identity: CabinetIdentity): Promise<CabinetBookings> {
    const empty: CabinetBookings = { state: 'unavailable', items: [], error: '', note: '' };
    if (!psyAvailable) {
        return {
            ...empty,
            note: 'Запись к специалисту доступна при установленном модуле психологии (hydra.medic.psy).',
        };
    }
    if (!identity.psy) {
        return {
            ...empty,
            state: asSourceState(identity.psyState),
            error: identity.psyError,
            note: identity.psyNote || 'Карточка клиента не связана — записи недоступны.',
        };
    }
    try {
        const [response, statusLabels] = await Promise.all([
            getSDK().metadata.listTableData({
                schema: 'public',
                table: PSY_BOOKINGS_TABLE,
                pageSize: 300,
                page: 0,
                filters: [{ column: 'client_id', value: identity.psy.id }],
            }),
            loadBookingStatusLabels(),
        ]);
        const items = rowsOf(response.rows).map((row): CabinetBooking => {
            const statusCode = row.status || '';
            const duration = Number(row.duration_min || '');
            return {
                id: row.id,
                startAt: row.start_at || '',
                durationMin: Number.isFinite(duration) && duration > 0 ? duration : null,
                serviceName: row.service_id_presentation || '',
                specialistName: row.specialist_id_presentation || '',
                statusCode,
                statusLabel: statusLabels[statusCode] || row.status_presentation || statusCode || '—',
                comment: row.comment || '',
            };
        });
        items.sort((a, b) => {
            const aKey = timestampKey(a.startAt);
            const bKey = timestampKey(b.startAt);
            const now = timestampKey(new Date().toISOString());
            const aUpcoming = aKey >= now;
            const bUpcoming = bKey >= now;
            if (aUpcoming && bUpcoming) return aKey.localeCompare(bKey);
            if (aUpcoming) return -1;
            if (bUpcoming) return 1;
            return bKey.localeCompare(aKey);
        });
        return { state: 'ready', items, error: '', note: '' };
    } catch (e) {
        const mapped = sourceState(e);
        return {
            ...empty,
            state: mapped.state,
            error: mapped.error || (mapped.state === 'denied' ? 'Нет прав на чтение записей' : ''),
            note: mapped.state === 'unavailable'
                ? 'Документ записей на приём не найден (psy_bookings).'
                : mapped.state === 'denied'
                    ? 'Записи к специалисту недоступны роли «Пациент» — право «Чтение» выдаёт администратор базы.'
                    : '',
        };
    }
}

async function loadBookingStatusLabels(): Promise<Record<string, string>> {
    try {
        const response = await getSDK().metadata.getEnum({ code: PSY_BOOKING_STATUS_ENUM });
        const labels: Record<string, string> = {};
        for (const item of response.values || []) {
            labels[String(item.value)] = String(item.synonym || item.name || item.value);
        }
        return labels;
    } catch (e) {
        if (!isNotFoundError(e)) logSwallowed(e, 'Кабинет пациента: подписи записей psy_bookings');
        return {};
    }
}

async function loadEcg(identity: CabinetIdentity, userId: string): Promise<CabinetEcg> {
    const empty: CabinetEcg = { state: 'unavailable', studies: [], error: '', note: '' };
    if (!userId) {
        return { ...empty, state: 'error', error: 'Не определён текущий пользователь' };
    }
    try {
        const patientColumn = await resolveEcgPatientColumn();
        if (patientColumn && identity.medic) {
            const studies = await listEcgStudies({
                patient: { id: identity.medic.id, source: 'patient' },
                limit: 500,
            });
            return {
                state: 'ready',
                studies,
                error: '',
                note: 'История ЭКГ связана с карточкой пациента.',
            };
        }
        const studies = await listEcgStudies({ ownerId: userId, limit: 500 });
        return {
            state: 'ready',
            studies,
            error: '',
            note: patientColumn
                ? 'Карточка пациента не связана — показаны исследования, где вы указаны владельцем.'
                : 'В базе нет реквизита пациента у исследований — показаны ваши исследования.',
        };
    } catch (e) {
        const mapped = sourceState(e);
        return {
            ...empty,
            state: mapped.state,
            error: mapped.error || (mapped.state === 'denied' ? 'Нет прав на чтение исследований ЭКГ' : ''),
            note: mapped.state === 'unavailable'
                ? 'Модуль ЭКГ не установлен (справочник cardio_studies отсутствует).'
                : mapped.state === 'denied'
                    ? 'Исследования ЭКГ доступны только при выдаче права «Чтение» роли «Пациент».'
                    : '',
        };
    }
}

function buildInvitations(tests: CabinetTests): CabinetInvitation[] {
    if (tests.kind !== 'psy' || tests.assignmentsState !== 'ready') return [];
    const methodByCode = new Map(tests.methods.map((method) => [method.code, method]));
    return tests.assignments
        .map((assignment): CabinetInvitation => {
            const method = methodByCode.get(assignment.methodCode);
            const link = activeLink(assignment);
            return {
                id: assignment.id,
                methodName: method?.synonym || assignment.methodCode || 'Методика',
                methodCode: assignment.methodCode,
                status: assignment.status,
                statusLabel: assignmentStatusLabel(assignment.status),
                code: link ? shortCode(link.token).toUpperCase() : '',
                token: link?.token || '',
                expiresAt: link?.expiresAt || assignment.linkExpiresAt,
                active: !!link,
                expired: !!assignment.linkToken && !link,
            };
        })
        .filter((invitation) => invitation.active || invitation.status === 'assigned')
        .sort((a, b) => Number(b.active) - Number(a.active));
}

function detailOf(state: CabinetIdentityState, readyText: string, error?: string): string {
    if (error) return error;
    if (state === 'ready') return readyText;
    if (state === 'denied') return 'Нет доступа — право «Чтение» выдаёт администратор базы.';
    if (state === 'unavailable') return 'Модуль или данные не установлены в базе.';
    if (state === 'unlinked') return 'Карточка не связана с учётной записью.';
    if (state === 'ambiguous') return 'Найдено несколько карточек — требуется уточнение.';
    return 'Ошибка загрузки — повторите попытку.';
}

function buildAccess(
    identity: CabinetIdentity,
    tests: CabinetTests,
    encounters: CabinetEncounters,
    bookings: CabinetBookings,
    ecg: CabinetEcg,
    invitations: CabinetInvitation[],
): CabinetAccessRow[] {
    const cardState: CabinetIdentityState = identity.psyState === 'ready' || identity.medicState === 'ready'
        ? 'ready'
        : identity.psyState !== 'unlinked'
            ? identity.psyState
            : identity.medicState;
    const cardNames = [identity.psy?.name, identity.medic?.name].filter(Boolean).join(' · ');
    const cardError = identity.psyError || identity.medicError
        || identity.psyNote || identity.medicNote;
    const testsCounts = tests.kind === 'psy'
        ? `назначений: ${tests.assignments.length} · результатов: ${tests.results.length}`
        : tests.poll
            ? `доступных тестов: ${tests.poll.tests.length} · результатов: ${tests.poll.protocols.length}`
            : 'нет данных';
    const testsDetail = tests.state === 'ready'
        ? `${testsCounts}${tests.resultsState === 'denied' ? ' · часть результатов ограничена правами' : ''}`
        : tests.error;
    return [
        {
            key: 'card',
            title: 'Карточка пациента',
            state: cardState,
            // Для готовой связи показываем ИМЕНА карточек, а не служебную
            // заметку (заметка — только когда связи нет/она неоднозначна).
            detail: detailOf(
                cardState,
                cardNames || 'Карточка связана',
                cardState === 'ready' ? '' : cardError,
            ),
        },
        {
            key: 'tests',
            title: 'Тесты и результаты',
            state: tests.state,
            detail: detailOf(tests.state, testsDetail, tests.error || tests.note),
        },
        {
            key: 'encounters',
            title: 'Приёмы',
            state: encounters.state,
            detail: detailOf(encounters.state, `записей: ${encounters.items.length}`, encounters.error || encounters.note),
        },
        {
            key: 'bookings',
            title: 'Запись к специалисту',
            state: bookings.state,
            detail: detailOf(bookings.state, `записей: ${bookings.items.length}`, bookings.error || bookings.note),
        },
        {
            key: 'ecg',
            title: 'Исследования ЭКГ',
            state: ecg.state,
            detail: detailOf(ecg.state, `исследований: ${ecg.studies.length}`, ecg.error || ecg.note),
        },
        {
            key: 'invitations',
            title: 'Приглашения на тестирование',
            state: tests.kind === 'psy' ? tests.assignmentsState : 'unavailable',
            detail: tests.kind === 'psy'
                ? detailOf(
                    tests.assignmentsState,
                    invitations.length > 0
                        ? `активных приглашений: ${invitations.filter((invitation) => invitation.active).length}`
                        : 'действующих приглашений нет',
                    tests.assignmentsError,
                )
                : 'Приглашения на тестирование выдаёт специалист (модуль психологии не установлен).',
        },
    ];
}

/**
 * loadPatientCabinetDashboard — единая загрузка кабинета (SRP: экран только
 * рендерит). Разделы изолированы: отказ/ошибка одного не скрывает остальные.
 */
export async function loadPatientCabinetDashboard(user: {
    id?: string;
    name?: string;
    login?: string;
    phone?: string;
    email?: string;
    roles?: string[];
    permissions?: string[];
} | null): Promise<PatientCabinetDashboard> {
    const userId = String(user?.id || '');
    const phone = String(user?.phone || '');
    const email = String(user?.email || '');
    const roles = user?.roles || [];
    const isAdmin = roles.includes('admin') || (user?.permissions || []).includes('global:admin');

    let psyAvailable = false;
    try {
        psyAvailable = await hasPsyAssignmentsModule();
    } catch (e) {
        logSwallowed(e, 'patientCabinet: проверка модуля психологии');
    }

    const [psy, medic] = await Promise.all([
        psyAvailable
            ? resolvePsyIdentity(userId, phone, email)
            : Promise.resolve({
                psy: null,
                psyState: 'unavailable' as CabinetIdentityState,
                psyError: '',
                psyNote: 'Модуль психологии не установлен.',
            }),
        resolveMedicIdentity(userId, phone, email),
    ]);

    const identity: CabinetIdentity = {
        userId,
        name: String(user?.name || ''),
        login: String(user?.login || ''),
        phone,
        email,
        roles,
        isAdmin,
        patientRole: roles.includes('patient'),
        ...psy,
        ...medic,
    };

    const tests = await loadTests(userId, identity, psyAvailable);
    // Общий кабинет опросов уже прочитал карточку клиента (loadMyCard) —
    // используем её, если отдельный резолв psy не дал результата.
    if (!identity.psy && tests.poll) {
        if (tests.poll.card) {
            identity.psy = {
                id: tests.poll.card.id,
                name: tests.poll.card.name,
                phone: tests.poll.card.phone,
                email: tests.poll.card.email,
                linkedUserId: userId,
                matchedBy: 'account',
            };
            identity.psyState = 'ready';
            identity.psyNote = 'Карточка клиента загружена кабинетом опросов.';
        } else if (tests.poll.cardDenied) {
            identity.psyState = 'denied';
            identity.psyNote = 'Нет прав на чтение карточки клиента.';
        } else if (tests.poll.cardAmbiguous) {
            identity.psyState = 'ambiguous';
            identity.psyNote = 'К учётной записи привязано несколько карточек клиента — обратитесь к специалисту.';
        }
    }
    const [encounters, bookings, ecg] = await Promise.all([
        loadEncounters(identity),
        loadBookings(psyAvailable, identity),
        loadEcg(identity, userId),
    ]);
    const invitations = buildInvitations(tests);
    return {
        identity,
        tests,
        encounters,
        bookings,
        ecg,
        invitations,
        access: buildAccess(identity, tests, encounters, bookings, ecg, invitations),
    };
}
